import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Asset, Message, Portfolio, Quote, Report, Snapshot, SymbolIndexRecord, Thread } from '@/types';

interface AssetListDB extends DBSchema {
  portfolios: { key: string; value: Portfolio };
  assets: { key: string; value: Asset; indexes: { byPortfolio: string } };
  quotes: { key: string; value: Quote };
  snapshots: { key: string; value: Snapshot };
  reports: { key: string; value: Report; indexes: { byDate: string } };
  threads: { key: string; value: Thread; indexes: { byUpdated: number } };
  messages: { key: string; value: Message; indexes: { byThread: string } };
  symbolIndex: { key: string; value: SymbolIndexRecord };
}

let dbPromise: Promise<IDBPDatabase<AssetListDB>> | undefined;

function db() {
  dbPromise ??= openDB<AssetListDB>('assetlist', 3, {
    upgrade(database, oldVersion) {
      if (oldVersion < 1) {
        database.createObjectStore('portfolios', { keyPath: 'id' });
        database.createObjectStore('assets', { keyPath: 'id' }).createIndex('byPortfolio', 'portfolioId');
        database.createObjectStore('quotes', { keyPath: 'key' });
        database.createObjectStore('snapshots', { keyPath: 'date' });
        database.createObjectStore('threads', { keyPath: 'id' }).createIndex('byUpdated', 'updatedAt');
        database.createObjectStore('messages', { keyPath: 'id' }).createIndex('byThread', 'threadId');
      }
      // v2: cached provider symbol listing for local ticker autocomplete (a cache, not user data).
      if (oldVersion < 2) database.createObjectStore('symbolIndex', { keyPath: 'key' });
      // v3: report templates replace the pre/post-market brief; old briefs are dropped (they were day-cached, not archival).
      if (oldVersion < 3) {
        const raw = database as unknown as IDBDatabase;
        if (raw.objectStoreNames.contains('briefs')) raw.deleteObjectStore('briefs');
        database.createObjectStore('reports', { keyPath: 'id' }).createIndex('byDate', 'date');
      }
    },
  });
  return dbPromise;
}

export const repo = {
  async loadAll() {
    const d = await db();
    const [portfolios, assets, quotes, snapshots, reports, threads] = await Promise.all([
      d.getAll('portfolios'),
      d.getAll('assets'),
      d.getAll('quotes'),
      d.getAll('snapshots'),
      d.getAll('reports'),
      d.getAllFromIndex('threads', 'byUpdated'),
    ]);
    return { portfolios: portfolios.sort((a, b) => a.sortOrder - b.sortOrder), assets, quotes, snapshots, reports, threads: threads.reverse() };
  },

  async putPortfolio(p: Portfolio) { await (await db()).put('portfolios', p); },
  async deletePortfolio(id: string) {
    const tx = (await db()).transaction(['portfolios', 'assets'], 'readwrite');
    await tx.objectStore('portfolios').delete(id);
    for (const key of await tx.objectStore('assets').index('byPortfolio').getAllKeys(id)) await tx.objectStore('assets').delete(key);
    await tx.done;
  },

  async putAsset(a: Asset) { await (await db()).put('assets', a); },
  async deleteAsset(id: string) { await (await db()).delete('assets', id); },

  async putQuotes(quotes: Quote[]) {
    const tx = (await db()).transaction('quotes', 'readwrite');
    await Promise.all(quotes.map((q) => tx.store.put(q)));
    await tx.done;
  },

  async putSnapshot(s: Snapshot) { await (await db()).put('snapshots', s); },
  async putReport(r: Report) { await (await db()).put('reports', r); },
  async deleteReport(id: string) { await (await db()).delete('reports', id); },

  async putThread(t: Thread) { await (await db()).put('threads', t); },
  async deleteThread(id: string) {
    const tx = (await db()).transaction(['threads', 'messages'], 'readwrite');
    await tx.objectStore('threads').delete(id);
    for (const key of await tx.objectStore('messages').index('byThread').getAllKeys(id)) await tx.objectStore('messages').delete(key);
    await tx.done;
  },
  async listMessages(threadId: string): Promise<Message[]> {
    const list = await (await db()).getAllFromIndex('messages', 'byThread', threadId);
    return list.sort((a, b) => a.seq - b.seq);
  },
  async putMessage(m: Message) { await (await db()).put('messages', m); },

  async getSymbolIndex(key: string): Promise<SymbolIndexRecord | undefined> { return (await db()).get('symbolIndex', key); },
  async putSymbolIndex(rec: SymbolIndexRecord) { await (await db()).put('symbolIndex', rec); },

  async exportAll() {
    const d = await db();
    const [portfolios, assets, snapshots, reports, threads, messages] = await Promise.all([
      d.getAll('portfolios'), d.getAll('assets'), d.getAll('snapshots'), d.getAll('reports'), d.getAll('threads'), d.getAll('messages'),
    ]);
    return { exportedAt: new Date().toISOString(), version: 2, portfolios, assets, snapshots, reports, threads, messages };
  },
  async importAll(data: { portfolios?: Portfolio[]; assets?: Asset[]; snapshots?: Snapshot[]; reports?: Report[]; threads?: Thread[]; messages?: Message[] }) {
    const tx = (await db()).transaction(['portfolios', 'assets', 'snapshots', 'reports', 'threads', 'messages'], 'readwrite');
    await Promise.all([
      ...(data.portfolios ?? []).map((x) => tx.objectStore('portfolios').put(x)),
      ...(data.assets ?? []).map((x) => tx.objectStore('assets').put(x)),
      ...(data.snapshots ?? []).map((x) => tx.objectStore('snapshots').put(x)),
      ...(data.reports ?? []).map((x) => tx.objectStore('reports').put(x)),
      ...(data.threads ?? []).map((x) => tx.objectStore('threads').put(x)),
      ...(data.messages ?? []).map((x) => tx.objectStore('messages').put(x)),
    ]);
    await tx.done;
  },
  async clearAll() {
    const stores = ['portfolios', 'assets', 'quotes', 'snapshots', 'reports', 'threads', 'messages', 'symbolIndex'] as const;
    const tx = (await db()).transaction(stores, 'readwrite');
    await Promise.all(stores.map((s) => tx.objectStore(s).clear()));
    await tx.done;
  },
  async estimateUsage(): Promise<number | null> {
    try {
      return (await navigator.storage?.estimate?.())?.usage ?? null;
    } catch {
      return null;
    }
  },
};
